import { type FC } from "react";
import { cn } from "../lib/utils";
import { _BaseButton, type _BaseButtonProps } from "./_BaseButton";
import { Text, type TextProps } from "./Text";

export type ClickableTextProps = Omit<_BaseButtonProps, "color" | "type"> & {
	/**
	 * Semantic color of the text, from the `Icon`-aligned token map. Passed straight through
	 * to the inner `Text`. Omit to inherit the surrounding text color.
	 */
	color?: TextProps["color"];

	/**
	 * Typographic style of the text, passed straight through to the inner `Text`. Each type
	 * pairs with an entry in `Icon`'s `sizeMap` so text and icons tone from the same scale:
	 * - `display` ↔ Icon `large`
	 * - `heading` ↔ Icon `medium`
	 * - `title` ↔ Icon `small`
	 * - `body` / `body-strong` ↔ Icon `mini`
	 * - `caption` / `eyebrow` ↔ Icon `micro`
	 *
	 * Omit to inherit the surrounding font size.
	 */
	type?: TextProps["type"];
};

/**
 * Inline, text-styled button primitive. Renders a `Text` inside a `_BaseButton`, so the
 * tracking/analytics wiring and native `<button>` semantics (keyboard, focus, `onClick`,
 * `disabled`, `aria-*`) come for free while presenting as inline text rather than a chrome'd
 * button. The `color`/`type` props are passed straight through to the inner `Text`;
 * `ClickableText` adds only the interactive layer — a fade-and-underline hover.
 *
 * Reach for `Button` when you need a chrome'd, sized call-to-action; reach for
 * `ClickableText` for link-like affordances embedded in text or compact UI.
 */
export const ClickableText: FC<ClickableTextProps> = ({
	color,
	type,
	className,
	children,
	ref,
	...rest
}) => (
	<_BaseButton
		ref={ref}
		className={cn(
			"inline cursor-pointer text-left disabled:cursor-not-allowed disabled:opacity-[0.45] disabled:pointer-events-none",
			className,
		)}
		{...rest}
	>
		<Text color={color} type={type} className="transition-opacity hover:underline hover:opacity-90">
			{children}
		</Text>
	</_BaseButton>
);
ClickableText.displayName = "ClickableText";
